import React, { useEffect, useState } from "react";
import AppWrapper from "../AppWrapper";
import { Banner, Button, IndexTable, LegacyCard, Modal, Page, Spinner, Text, Toast } from "@shopify/polaris";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

function Users() {
  const navigate = useNavigate();
  const { claims } = UserAuth();
  const role = claims.role;
  const [users, setUsers] = useState(undefined);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [toast, setToast] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      // Sucursal needs users.branch_id (20261026_car_washes.sql).
      let { data, error } = await supabase
        .from("users")
        .select("*, branch:branches(name)")
        .is("deleted_at", null);
      if (error) ({ data, error } = await supabase.from("users").select("*").is("deleted_at", null));
      if (error) {
        console.error(error);
        return;
      }
      setUsers(data);
    };
    if (!users) fetchData();
  }, []);

  const rowDetail = users?.map((user) => (
    <IndexTable.Row
      key={user.id}
      id={`user-${user.email}`}
      onClick={() => {
        if (role !== "superadmin") return;
        navigate(`/users/${user.id}`);
      }}
    >
      <IndexTable.Cell>
        <Text>{user.name}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text>{user.email}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text>{user.branch?.name || "Todas"}</Text>
      </IndexTable.Cell>
      {role === "superadmin" && (
        <IndexTable.Cell>
          {user.id !== claims.sub && (
            // Keep the click from opening the user.
            <div onClick={(e) => e.stopPropagation()}>
              <Button
                variant="plain"
                tone="critical"
                onClick={() => {
                  setDeleteError("");
                  setToDelete(user);
                }}
              >
                Eliminar
              </Button>
            </div>
          )}
        </IndexTable.Cell>
      )}
    </IndexTable.Row>
  ));

  const pageContent = () => {
    if (!users) return <Spinner size="small" />;
    return (
      <LegacyCard>
        <IndexTable
          selectable={false}
          itemCount={users?.length ?? 0}
          headings={[
            { title: "Nombre" },
            { title: "Correo" },
            { title: "Sucursal" },
            ...(role === "superadmin" ? [{ title: "" }] : []),
          ]}
        >
          {rowDetail}
        </IndexTable>
      </LegacyCard>
    );
  };

  const deleteUser = async () => {
    setDeleting(true);
    setDeleteError("");
    const { data, error } = await supabase.functions.invoke("delete_user", {
      body: { user_id: toDelete.id },
    });
    setDeleting(false);
    if (error || data?.error) {
      let message = data?.error;
      if (!message && error?.context?.json) message = (await error.context.json().catch(() => null))?.error;
      setDeleteError(message || error?.message || "No se pudo eliminar el usuario");
      return;
    }
    setUsers((prev) => prev.filter((u) => u.id !== toDelete.id));
    setToast(`${toDelete.name || toDelete.email} eliminado`);
    setToDelete(null);
  };

  return (
    <AppWrapper>
      <Page title="Usuarios">{pageContent()}</Page>
      <Modal
        open={!!toDelete}
        onClose={() => !deleting && setToDelete(null)}
        title={`Eliminar a ${toDelete?.name || toDelete?.email || ""}`}
        primaryAction={{
          content: "Eliminar",
          destructive: true,
          onAction: deleteUser,
          loading: deleting,
        }}
        secondaryActions={[{ content: "Cancelar", onAction: () => setToDelete(null), disabled: deleting }]}
      >
        <Modal.Section>
          <Text>
            Ya no podrá iniciar sesión ni usar su PIN, y se le quitarán sus permisos. Su nombre se
            conserva en el historial (reservaciones, pagos, inspecciones…).
          </Text>
          {deleteError && (
            <div style={{ marginTop: 12 }}>
              <Banner tone="critical">{deleteError}</Banner>
            </div>
          )}
        </Modal.Section>
      </Modal>
      {toast && <Toast content={toast} onDismiss={() => setToast("")} />}
    </AppWrapper>
  );
}

export default Users;
